
import React, { CSSProperties, useRef,} from 'react';
import './index.module.less';
import { TNotification, ENotificationStyle, TNotificationBodyData, TNotificationPhoto, TNotificationAction } from '@apis/notification/getNotificationList';
import { rem } from '@helpers/Constants';
import Toast from '@widgets/Toast';
import Http from '@helpers/Http';
import { Api } from '@apis/client';
import ConfirmModal from '@views/components/ConfirmModal';
import { observer } from 'mobx-react';
import notificationStore from '@stores/notification';
import PopupMenu from '@widgets/PopupMenu';
import PageNavigator from '@helpers/PageNavigator';
import { handleStylePx2rem } from '@helpers/utils';
import callUtils from '@utils/callUtil';
import { removeUrlParam } from '@helpers/urlUtils';
import userStore from '@stores/user';
interface IProps {
  itemInfo: TNotification;
  onDeleteItem: (item: TNotification) => void;
}

const NotificationItem = (props: IProps) => {
  const { itemInfo, onDeleteItem } = props;
  const actionAnchorRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<any>(null);
  const longPressAnchorRef = useRef<HTMLDivElement>(null);

  /// Common Action Info - Begin ///
  const hasActionWidget = () => {
    const findAction = (itemInfo.actionInfo?.action1
      || itemInfo.actionInfo?.action2
      || itemInfo.actionInfo?.action3);
    return findAction !== null && findAction !== undefined;
  }

  const countActionWidget = () => {
    let count = 0;
    if (itemInfo.actionInfo?.action1) count++;
    if (itemInfo.actionInfo?.action2) count++;
    if (itemInfo.actionInfo?.action3) count++;
    return count;
  }

  const getSingleAction = () => {
    return itemInfo.actionInfo?.action1 || itemInfo.actionInfo?.action2 || itemInfo.actionInfo?.action3;
  }
  /// Common Action Info - End ///

  /// Common Photo Info - Begin ///
  const getSinglePhoto = () => {
    return itemInfo.photoInfo?.photo1 ||
      itemInfo.photoInfo?.photo2 ||
      itemInfo.photoInfo?.photo3 ||
      itemInfo.photoInfo?.photo4 ||
      itemInfo.photoInfo?.photo5 ||
      itemInfo.photoInfo?.photo6;
  }
  /// Common Photo Info - End///



  /// Notification Style - Begin ///
  const buildTitleAndPopupAction = () => {
    const tipColorValue = itemInfo.bodyInfo?.alertData?.contentColor;
    let tipColor = tipColorValue ? tipColorValue : '#F53F3F';
    tipColor = itemInfo.hasRead === false ? tipColor : '#1C1D1E';
    let tipText = itemInfo.bodyInfo?.alertData?.content;
    return (
      <div styleName='title-and-pop-action'>
        <div styleName='title-tip'>
          {tipText && <div styleName='tip' style={{ color: tipColor }}>{tipText}</div>}
          <div styleName='title'>{itemInfo.title}</div>
        </div>
        <i className="iconfont icon-icon-more" styleName='pop-action' ref={actionAnchorRef} onClick={buildPopupMenuAction} />
      </div>
    );
  }


  const buildPopupMenuAction = (event: any) => {
    event?.stopPropagation();
    const popupMenuActionList = itemInfo.actionInfo?.popupMenuAction;
    const actionPopupMenuList = popupMenuActionList?.map((item: any, index: number) => {
      return {
        title: item.name,
        value: index,
      }
    }) || [];
    actionPopupMenuList.push({
      title: '删除通知',
      value: -1
    });
    PopupMenu.show({
      width: 160,
      items: actionPopupMenuList,
      anchor: actionAnchorRef,
      onTap(_, value: number) {
        if (value === -1) {
          popConfirmDelete();
        } else {
          handleClickAction(itemInfo, popupMenuActionList[value], null);
        }
      },
    });
  };

  const popConfirmDelete = () => {
    ConfirmModal.show({
      title: '确定删除通知',
      text: '删除后，将无法恢复。',
      confirmTheme: 'warn',
      showCancelBtn: true,
      confirmBtnText: '删 除',
      onConfirm: () => {
        onDeleteItem(itemInfo);
      },
    });
  }

  const buildBodyHeadData = () => {
    const content = itemInfo.bodyInfo?.headData?.content;
    const contentColorValue = itemInfo.bodyInfo?.headData?.contentColor || '#1C1D1E';
    if (content) {
      return <div styleName='body-head-data' style={{ color: contentColorValue }}>{content}</div>
    }
    return null;
  }

  const buildBodyDetailData = () => {
    const bodyDataWidgetList: JSX.Element[] = [];
    const targetChild = buildExpandableBodyData(bodyDataWidgetList);
    return (
      <div styleName='body-detail-and-cover'>
        {targetChild || <div />}
        {buildCoverPhoto()}
      </div>
    );
  }

  const buildExpandableBodyData = (bodyDataWidgetList: JSX.Element[]) => {
    handleBodyDataWidget(bodyDataWidgetList, itemInfo.bodyInfo?.data1);
    handleBodyDataWidget(bodyDataWidgetList, itemInfo.bodyInfo?.data2);
    handleBodyDataWidget(bodyDataWidgetList, itemInfo.bodyInfo?.data3);
    handleBodyDataWidget(bodyDataWidgetList, itemInfo.bodyInfo?.data4);
    handleBodyDataWidget(bodyDataWidgetList, itemInfo.bodyInfo?.data5);
    handleBodyDataWidget(bodyDataWidgetList, itemInfo.bodyInfo?.data6);
    return bodyDataWidgetList.length > 0 ? <div styleName='body-detail-wrapper '>{bodyDataWidgetList}</div> : null;
  }

  const handleBodyDataWidget = (widgetList: JSX.Element[], bodyData?: TNotificationBodyData) => {
    if (bodyData) {
      const label = (bodyData.label && `${bodyData.label}：`) || '';
      const contentColorValue = bodyData.contentColor || '#1C1D1E';
      widgetList.push(
        <div key={widgetList.length} styleName='body-detail'>
          <span styleName='body-detail-label'>{label}</span>
          <span styleName='body-detail-content' style={{ color: contentColorValue }}>{bodyData.content}</span>
        </div>
      );
    }
  }

  const buildCoverPhoto = () => {
    const imageUrl = itemInfo.photoInfo?.coverPhoto?.url;
    if (imageUrl) {
      return buildPhoto(itemInfo.photoInfo.coverPhoto, 80, 80);
    }
    return null;
  }

  const buildPhoto = (photo: TNotificationPhoto, width?: number, height?: number) => {
    const imageUrl = photo.url;
    if (imageUrl) {
      const hasCustomSize = (width || height) > 0;
      const hasClickAction = photo.action !== undefined && photo.action !== null;
      let photoStyle: React.CSSProperties = {};
      if (hasCustomSize) {
        const widthRem = width ? width * 0.02 * rem : null;
        const heightRem = height ? height * 0.02 * rem : null;
        photoStyle = {
          width: width ? widthRem : null,
          height: height ? heightRem : null
        }
      }
      return (
        <img
          onClick={hasClickAction ? (event) => {
            handleClickAction(itemInfo, photo.action, event);
          } : null}
          styleName='photo'
          style={photoStyle}
          src={imageUrl}
          alt=''
        />
      );
    }
    return null;
  }

  const buildBodyPhotoList = () => {
    const imageWidgetList: JSX.Element[] = [];
    handleAddPhotoWidget(imageWidgetList, itemInfo.photoInfo?.photo1);
    handleAddPhotoWidget(imageWidgetList, itemInfo.photoInfo?.photo2);
    handleAddPhotoWidget(imageWidgetList, itemInfo.photoInfo?.photo3);
    handleAddPhotoWidget(imageWidgetList, itemInfo.photoInfo?.photo4);
    handleAddPhotoWidget(imageWidgetList, itemInfo.photoInfo?.photo5);
    handleAddPhotoWidget(imageWidgetList, itemInfo.photoInfo?.photo6);
    if (imageWidgetList.length === 0) {
      return null;
    }
    // 如果只有一张图片，那么就限定其高度，宽度自适应
    if (imageWidgetList.length === 1) {
      return <div styleName='photo-list'>{buildPhoto(getSinglePhoto(), null, 130)}</div>;
    }
    return (
      <div styleName='photo-list'>
        {imageWidgetList}
      </div>
    );
  }

  const handleAddPhotoWidget = (widgetList: JSX.Element[], photo?: TNotificationPhoto) => {
    if (photo?.url) {
      widgetList.push(<div key={`photo-${widgetList.length}`} styleName='photo-item'>{buildPhoto(photo)}</div>);
    }
  }

  const buildBodyTailData = () => {
    const tailData = itemInfo.bodyInfo?.tailData?.content;
    if (tailData) {
      return (
        <div styleName='body-tail'>{tailData}</div>
      );
    }
  }

  const buildBottomActionArea = () => {
    const actionWidgetList: JSX.Element[] = [];
    handleActionWidget(actionWidgetList, itemInfo, itemInfo.actionInfo?.action1);
    handleActionWidget(actionWidgetList, itemInfo, itemInfo.actionInfo?.action2);
    handleActionWidget(actionWidgetList, itemInfo, itemInfo.actionInfo?.action3);
    if (actionWidgetList.length === 0) {
      return null;
    }
    return (
      <div styleName='action-area-wrapper'>
        <div styleName='action-divider' />
        <div styleName='action-area'>{actionWidgetList}</div>
      </div>
    );
  }

  const handleActionWidget = (widgetList: JSX.Element[], notification: TNotification, action?: TNotificationAction) => {
    if (action && action.name) {
      if (widgetList.length > 0) {
        widgetList.push(<div key={'action-divider-' + action.name} styleName='divider' />);
      }
      widgetList.push(<div key={'action-button-' + action.name} styleName='action-button' onClick={(event) => {
        handleClickAction(notification, action, event);
      }}>{action.name}</div>);
    }
  }

  const handleClickAction = (notification: TNotification, action?: TNotificationAction, event?: React.MouseEvent) => {
    event?.stopPropagation();
    handleClickActionToCall(notification, action);
    handleClickActionToRoutePage(notification, action);
  }

  /// NotificationAction.contentCallback 表示当 Widget 被点击时，后台要执行的 ActionType，客户端需要把这个 contentCallback
  /// 传给后台
  const handleClickActionToCall = async (notification: TNotification, action?: TNotificationAction) => {
    if (action && action.name && action.contentCallback) {
      try {
        const result = await Http.post(Api.RequestActionByContentCallback, {
          recordUid: notification.uid,
          tplUid: notification.tplUid,
          tplType: notification.tplType,
          contentCallback: action.contentCallback!,
        });
        if (result?.data?.message) {
          Toast.show(result.data.message);
        }
      } catch (exception) {
        Toast.show('操作失败');
      }
    }
  }

  const handleClickActionToRoutePage = async (notification: TNotification, action?: TNotificationAction) => {

    // 标记该通知已读
    const markReadResult = await Http.post(Api.PostNotificationMarkRead, {
      recordUid: notification.uid,
      hasRead: true
    });

    if (markReadResult) {
      notificationStore.markNotificationHasRead(notification.uid);
    }

    // 旧的跳转权限校验 - 1.0.1
    if (action.path === '/notification/action' && action.pathExtras) {
      const deviceId = action.pathExtras['snCode'];
      const result = await Http.post(Api.GetDevicePhoneForUser, {
        uid: userStore.userInfo.uid,
        snCode: deviceId,
        company: 'lingge'
      });
      if (!result?.data) {
        ConfirmModal.show({
          title: '提示',
          text: '暂无该学生证权限',
          confirmBtnText: '好的',
        });
      } else {
        // 拨打电话
        callUtils.call(deviceId);
      }
      return;
    }

    // 新的跳转权限校验 - 1.0.2+
    const needCheckPermission = notification && (notification.needValidate || false);
    if (needCheckPermission) {
      // 是否具备权限
      const result = await Http.post(Api.PostCheckPermission, {
        unitUid: '',
        classUid: '',
        studentUid: '',
        recordUid: notification.uid,
        tplUid: notification.tplUid,
        tplType: notification.tplType,
        extraParam: action.pathExtras,
      });
      // 校验成功
      if (result.data) {
        handleGotoPageByUrl(action);
      } else {
        // 不具备权限
        ConfirmModal.show({
          title: '提示',
          text: result.message,
          confirmBtnText: '好的',
        });
      }
    } else {
      // 不需要校验，直接尝试跳转
      handleGotoPageByUrl(action);
    }
  }

  const handleGotoPageByUrl = (action?: TNotificationAction) => {
    if (action.h5Url) {
      PageNavigator.to({
        url: action.h5Url
      });
    } else if (action.url) {
      let targetUrl = removeUrlParam('webviewEnv', action.url);
      targetUrl = removeUrlParam('projectcode', targetUrl);
      PageNavigator.to({
        url: targetUrl
      });
    } else if (action.path === '/action/call/card') {
      const { snCode } = action.pathExtras;
      callUtils.call(snCode);
    } else {
      Toast.show({
        type: 'strip',
        title: '数据异常，地址无效'
      })
    }
  }

  const renderNotificationStyle = () => {
    const hasAction = hasActionWidget();
    return (
      <div styleName='notification-container' style={hasAction ? { paddingBottom: 0 } : {}}>
        {buildTitleAndPopupAction()}
        {buildBodyHeadData()}
        {buildBodyDetailData()}
        {buildBodyPhotoList()}
        {buildBodyTailData()}
        {buildBottomActionArea()}
      </div>
    );
  }
  /// Notification Style - End ///


  /// Card Style - Begin ///
  const renderCardStyle = () => {
    const titleWidget = buildCardStyleTitle();
    const descWidget = buildCardStyleDesc();
    const onlyHasPhoto = titleWidget == null && descWidget == null;

    return (
      <div styleName='card-style-container'>
        {buildCardStylePhoto(onlyHasPhoto)}
        {onlyHasPhoto ? null : <div styleName='card-info'>
          {titleWidget}
          {descWidget}
        </div>}
      </div>
    );
  }

  const buildCardStylePhoto = (onlyHasPhoto: boolean) => {
    const imageUrl = itemInfo.photoInfo?.coverPhoto?.url;
    const imageStyle: CSSProperties = onlyHasPhoto ? {} : {
      borderRadius: 0,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    }
    if (imageUrl) {
      return <img styleName='card-image' style={handleStylePx2rem(imageStyle)} src={imageUrl} alt='' />;
    }
    return null;
  }

  const buildCardStyleTitle = () => {
    const title = itemInfo.title;
    if (title) {
      return <div styleName='card-title'>{title}</div>
    }
    return null;
  }

  const buildCardStyleDesc = () => {
    const content = itemInfo.bodyInfo?.headData?.content;
    const contentColorValue = itemInfo.bodyInfo?.headData?.contentColor || '#7F838A';
    if (content) {
      return <div styleName='card-desc' style={{ color: contentColorValue }}>{content}</div>
    }
    return null;
  }
  /// Card Style - End ///

  const onTouchStart = (event: any) => {
    longPressTimer.current = setTimeout(() => {
      PopupMenu.show({
        width: 160,
        items: [{title: '删除通知'}],
        anchor: longPressAnchorRef,
        anchorMargin: -longPressAnchorRef.current.clientHeight / 2 - 20,
        onTap(_, value: number) {
          popConfirmDelete();
        },
      });
    }, 1000);
  }

  const onTouchEnd = () => {
    clearTimeout(longPressTimer.current);
  }

  const isCardStyle = itemInfo.msgStyle === ENotificationStyle.card;
  const wholeItemClickFunction = (countActionWidget() === 1) ? handleClickAction : null;
  return (<div key={itemInfo.uid} ref={longPressAnchorRef} onClick={(event) => {
    wholeItemClickFunction && wholeItemClickFunction(itemInfo, getSingleAction(), event);
  }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
    {isCardStyle ? renderCardStyle() : renderNotificationStyle()}
  </div>);
}

export default observer(NotificationItem);